import { useMemo, useCallback, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useEndpoints } from '../../hooks/useEndpoints';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { normalizeTestValue } from '../../utils/testing-calculations';

interface UseAthleteTestingViewDataProps {
  athleteId: string;
  selectedDate: string;
  categoryTests: ITestDefinition[];
  teamId?: string;
}

export function useAthleteTestingViewData({
  athleteId,
  selectedDate,
  categoryTests,
  teamId,
}: UseAthleteTestingViewDataProps) {
  const endpoints = useEndpoints();

  // Stato locale per i valori in-progress prima del salvataggio (sez.7)
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  // Valori multi-metrica locali: chiave `${testId}__${metric}` (sez.7.3)
  const [localMetricValues, setLocalMetricValues] = useState<Record<string, string>>({});
  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});

  // ── useQueries: storico per-test per questo atleta (sez.7) ──
  // Stesso pattern di TestingView.tsx — evita le chiamate di hook in loop.
  const athleteResultQueries = useQueries({
    queries: categoryTests.map((test) => ({
      queryKey: ['testing-results-athlete', athleteId, test.id],
      queryFn: async () => {
        const { data } = await endpoints.getAthleteTestResults(athleteId, { testId: test.id });
        return { testId: test.id, results: data as ITestResult[] };
      },
      enabled: !!athleteId && !!test.id,
      staleTime: 1000 * 60 * 5,
    })),
  });

  // ── useQueries: risultati team nella data selezionata (per avg/percentile) ──
  const teamResultQueries = useQueries({
    queries: categoryTests.map((test) => ({
      queryKey: ['testing-results-team', teamId, test.id, selectedDate],
      queryFn: async () => {
        const { data } = await endpoints.getTeamTestResults(teamId!, {
          testId: test.id,
          date: selectedDate,
        });
        return { testId: test.id, results: data as ITestResult[] };
      },
      enabled: !!teamId && !!test.id && !!selectedDate,
      staleTime: 1000 * 30,
    })),
  });

  /**
   * Mappa testId → array storico ITestResult per questo atleta (ordinati data desc).
   * Allineamento matrice↔profilo (sez.7).
   */
  const athleteResultsByTestId = useMemo(() => {
    const map = new Map<string, ITestResult[]>();
    athleteResultQueries.forEach((q) => {
      if (q.data) {
        const sorted = [...q.data.results].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        map.set(q.data.testId, sorted);
      }
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteResultQueries.map((q) => q.dataUpdatedAt).join(',')]);

  /**
   * Mappa testId → array risultati team nella data selezionata.
   * Per calcolo avg/percentile (sez.7).
   */
  const teamResultsByTestId = useMemo(() => {
    const map = new Map<string, ITestResult[]>();
    teamResultQueries.forEach((q) => {
      if (q.data) {
        map.set(q.data.testId, q.data.results);
      }
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamResultQueries.map((q) => q.dataUpdatedAt).join(',')]);

  /** IDs dei test per cui l'atleta ha almeno un risultato (sez.7.4) */
  const doneTestIds = useMemo(() => {
    const s = new Set<string>();
    athleteResultsByTestId.forEach((results, testId) => {
      if (results.length > 0) s.add(testId);
    });
    return s;
  }, [athleteResultsByTestId]);

  /** Risultato per il test nella data selezionata (undefined se assente) */
  const getResult = useCallback(
    (testId: string): ITestResult | undefined => {
      const history = athleteResultsByTestId.get(testId) ?? [];
      return history.find((r) => r.date.startsWith(selectedDate));
    },
    [athleteResultsByTestId, selectedDate]
  );

  /** Storico completo risultati atleta per un test (ordinati data desc) */
  const getAllResults = useCallback(
    (
      testId: string,
      timeframeFilter?: { startDate: string; endDate: string }
    ): ITestResult[] => {
      let results = athleteResultsByTestId.get(testId) ?? [];
      if (timeframeFilter) {
        results = results.filter(
          (r) =>
            r.date >= timeframeFilter.startDate && r.date <= timeframeFilter.endDate
        );
      }
      return results;
    },
    [athleteResultsByTestId]
  );

  /** Unità canonica (defaultUnit) per testId — per normalizzare i confronti */
  const canonicalUnitByTestId = useMemo(() => {
    const map = new Map<string, string>();
    categoryTests.forEach((t) => map.set(t.id, t.defaultUnit));
    return map;
  }, [categoryTests]);

  /**
   * Valori scalari del team nella data selezionata per un test (sez.7).
   *
   * NORMALIZZAZIONE UNITÀ (2026-07-28, semantica canonica backend via
   * @leopart/calc): ogni valore viene portato all'unità canonica del test
   * (defaultUnit) prima di entrare in media/percentile; i valori con unità
   * non convertibile vengono ESCLUSI, come fa il backend in
   * testing.service. Prima il FE confrontava kg e lb crudi nello stesso
   * array → percentili diversi dal BE per team con unità miste. Fix VOLUTO.
   */
  const getTeamValues = useCallback(
    (testId: string): number[] => {
      const teamResults = teamResultsByTestId.get(testId) ?? [];
      const canonicalUnit = canonicalUnitByTestId.get(testId);
      return teamResults
        .filter((r) => r.value > 0)
        .map((r) => normalizeTestValue(r.value, r.unit, canonicalUnit))
        .filter((v): v is number => v !== null);
    },
    [teamResultsByTestId, canonicalUnitByTestId]
  );

  // ── Gestione valori locali (sez.7) ──

  const resetLocalForDate = useCallback(() => {
    setLocalValues({});
    setLocalMetricValues({});
  }, []);

  const getLocalScalar = useCallback(
    (testId: string): string => localValues[testId] ?? '',
    [localValues]
  );

  const setLocalScalar = useCallback(
    (testId: string, val: string) =>
      setLocalValues((prev) => ({ ...prev, [testId]: val })),
    []
  );

  const getLocalMetric = useCallback(
    (testId: string, metric: string): string =>
      localMetricValues[`${testId}__${metric}`] ?? '',
    [localMetricValues]
  );

  const setLocalMetric = useCallback(
    (testId: string, metric: string, val: string) =>
      setLocalMetricValues((prev) => ({
        ...prev,
        [`${testId}__${metric}`]: val,
      })),
    []
  );

  // ── Salvataggio scalare con postResult backend (sez.7) ──
  const handleEditScalar = useCallback(
    async (test: ITestDefinition, rawValue: string) => {
      const numValue = parseFloat(rawValue.replace(',', '.'));
      if (rawValue.trim() === '' || isNaN(numValue) || !teamId) return;

      setSavingCells((prev) => ({ ...prev, [test.id]: true }));
      try {
        await endpoints.postTestResult({
          testId: test.id,
          athleteId,
          teamId,
          date: selectedDate,
          value: numValue,
          unit: test.defaultUnit,
          ...(test.exerciseId && { exerciseId: test.exerciseId }),
        });
        // Pulizia valore locale dopo salvataggio (sez.7)
        setLocalValues((prev) => {
          const next = { ...prev };
          delete next[test.id];
          return next;
        });
      } finally {
        setSavingCells((prev) => {
          const next = { ...prev };
          delete next[test.id];
          return next;
        });
      }
    },
    [endpoints, athleteId, teamId, selectedDate]
  );

  // ── Salvataggio multi-metrica con postResult backend (sez.7.3) ──
  const handleEditMultiMetric = useCallback(
    async (test: ITestDefinition, values: Record<string, number>) => {
      if (Object.keys(values).length === 0 || !teamId) return;

      setSavingCells((prev) => ({ ...prev, [test.id]: true }));
      try {
        await endpoints.postTestResult({
          testId: test.id,
          athleteId,
          teamId,
          date: selectedDate,
          values,
          ...(test.exerciseId && { exerciseId: test.exerciseId }),
        });
        // Pulizia valori multi-metrica locali dopo salvataggio (sez.7.3)
        setLocalMetricValues((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => {
            if (k.startsWith(`${test.id}__`)) delete next[k];
          });
          return next;
        });
      } finally {
        setSavingCells((prev) => {
          const next = { ...prev };
          delete next[test.id];
          return next;
        });
      }
    },
    [endpoints, athleteId, teamId, selectedDate]
  );

  const isLoading =
    athleteResultQueries.some((q) => q.isLoading) ||
    teamResultQueries.some((q) => q.isLoading);

  return {
    doneTestIds,
    getResult,
    getAllResults,
    getTeamValues,
    isLoading,
    // Valori locali
    getLocalScalar,
    setLocalScalar,
    getLocalMetric,
    setLocalMetric,
    savingCells,
    resetLocalForDate,
    // Salvataggio backend
    handleEditScalar,
    handleEditMultiMetric,
  };
}
